import { Button, Card, Stack, Text } from '@mantine/core';
import { IconInbox, IconPlus } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { EMPTY_COPY, suggestedAdoptSource, type EmptyReason } from '../../domain/emptyReason.js';
import { labelOf, type EntityType } from '../../domain/labels.js';
import { ENVIRONMENTS_ROUTE } from '../../domain/routes.js';
import type { RowLabel } from '../../domain/rowLabel.js';
import { RowLabelInline } from '../Diff/RecordLabel.js';
import type { MatrixColumn } from './types.js';

/**
 * Six empty states that mean six different things, and must not look alike.
 *
 * The pair people get wrong is "everything is in sync" and "the filters exclude everything":
 * render a shrug for both and a healthy system is indistinguishable from a mistyped filter.
 * "Nothing pulled", "master is empty" and "no studios yet" are likewise different
 * instructions — pull, adopt, seed. `type` names which entity the copy is about (the Studios
 * page's studios, or the Games page's games); `parent` and `onNew` are the Games page's own
 * `studio_empty` case — a studio filtered to no master games under it.
 *
 * Which one applies is decided in `domain/emptyReason.ts`, from the stored per-environment counts.
 */
export const MatrixEmptyState = ({
    type,
    reason,
    columns,
    onClearFilters,
    parent,
    onNew,
    onAdopt,
    adopting,
}: {
    type: EntityType;
    reason: EmptyReason;
    columns: MatrixColumn[];
    onClearFilters: () => void;
    parent?: RowLabel | null;
    onNew?: { label: string; to: string };
    /** `master_empty`: adopt everything from the environment holding the most — the page confirms and runs it. */
    onAdopt?: (source: { environment: string; count: number }) => void;
    adopting?: boolean;
}) => {
    const copy = EMPTY_COPY[reason]({ plural: labelOf(type).plural.toLowerCase(), inParent: Boolean(parent) });
    const adoptSource = suggestedAdoptSource(columns);
    const adoptCount = columns.find((column) => column.name === adoptSource)?.counts?.env_only ?? 0;

    const action =
        copy.action === 'pull' ? (
            <Button component={Link} to={ENVIRONMENTS_ROUTE} variant="light">
                Go to Environments
            </Button>
        ) : copy.action === 'adopt' && adoptSource && onAdopt ? (
            <Button
                variant="light"
                color="blue"
                leftSection={<IconInbox size={16} stroke={1.7} />}
                loading={adopting}
                onClick={() => onAdopt({ environment: adoptSource, count: adoptCount })}
            >
                Adopt {adoptCount} from {adoptSource}
            </Button>
        ) : copy.action === 'clear' ? (
            <Button variant="light" onClick={onClearFilters}>
                Clear filters
            </Button>
        ) : copy.action === 'create' && onNew ? (
            <Button component={Link} to={onNew.to} variant="light" leftSection={<IconPlus size={15} stroke={1.7} />}>
                {onNew.label}
            </Button>
        ) : null;

    return (
        <Card p="xl">
            <Stack gap="xs" align="center">
                <Text fw={600} ta="center">
                    {copy.title}
                    {copy.namesParent && parent && (
                        <>
                            {' '}
                            <RowLabelInline label={parent} />
                        </>
                    )}
                </Text>
                {copy.body && (
                    <Text c="dimmed" fz="sm" ta="center">
                        {copy.body}
                    </Text>
                )}
                {action}
            </Stack>
        </Card>
    );
};
